import React from 'lib/react';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import clsx from 'lib/clsx';
import {
  EVClickAwayListener,
  EVPopper,
  EVList,
  EVListItem,
  EVListItemText,
  EVPaper,
  EVListItemIcon,
  EVExitToAppOutlinedIcon,
  EVLink,
} from 'lib/components';
import { Link } from 'lib/router';
import { signOutUser, isAuthorized } from 'utils/auth.utils';
import { isOrderingDisabledSelector, hasTrueDesignAppEntitlementSelector } from 'utils/appSelectors.utils';
import { useSelector } from 'react-redux';
import { getUserMenuOptions } from 'utils/app.utils';
import useStyles from './UserProfileStyle';

/**
 * A menu component showing user settings
 */
function UserMenuPopper(props) {
  const {
    handleMenuClickAway, open, anchorEl,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const isOrderingDisabled = useSelector(isOrderingDisabledSelector);
  /**
   * Sign out user.
   */
  const handleLogOut = () => {
    handleMenuClickAway();
    signOutUser();
  };

  const hasTrueDesignAppEntitlement = useSelector(hasTrueDesignAppEntitlementSelector);

  return (
    <EVPopper
      id="user-menu"
      className={classes.popper}
      placement="bottom-end"
      anchorEl={anchorEl}
      open={open}
      disablePortal
    >
      <EVClickAwayListener onClickAway={handleMenuClickAway}>
        <EVPaper
          className={clsx(
            classes.settingsBelow,
            classes.userMenu,
          )}
          elevation={6}
          component="nav"
        >
          <EVList disablePadding>
            {
              getUserMenuOptions(isOrderingDisabled, hasTrueDesignAppEntitlement)
                .map((menuOption) => {
                  const LinkComponent = menuOption.base ? EVLink : Link;
                  const linkProps = menuOption.base ? { href: `${menuOption.base}${menuOption.link}` } : { to: menuOption.link };
                  return (
                    <EVListItem key={menuOption.name} className={classes.listStyle}>
                      <LinkComponent onClick={handleMenuClickAway} aria-label={t(`${menuOption.name}`)} {...linkProps}>
                        {t(`${menuOption.name}`)}
                      </LinkComponent>
                    </EVListItem>
                  );
                })
            }
            {isAuthorized() && (
              <EVListItem id="signOut" className={classes.listStyle} onClick={handleLogOut} component="button" aria-label={t('LogOut')}>
                <EVListItemIcon className={classes.listItemIcon}>
                  <EVExitToAppOutlinedIcon className={classes.signOutIcon} />
                </EVListItemIcon>
                <EVListItemText primary={t('LogOut')} classes={{ primary: classes.listText }} />
              </EVListItem>
            )}
          </EVList>
        </EVPaper>
      </EVClickAwayListener>
    </EVPopper>
  );
}

UserMenuPopper.defaultProps = {
  anchorEl: null,
};

UserMenuPopper.propTypes = {

  /**
   * Function to be called on clicking outside the menu
   * @property {string} handleMenuClickAway
   */
  handleMenuClickAway: PropTypes.func.isRequired,

  /**
   * Toggles opening and closing of the menu
   * @property {bool} open
   */
  open: PropTypes.bool.isRequired,

  /**
   * Reference object for EVPopper
   * @property {element/object/func} anchorEl
   */
  anchorEl: PropTypes.oneOfType([PropTypes.element, PropTypes.object, PropTypes.func]),
};

export default UserMenuPopper;
